import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Grid, Paper, Typography } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { Link, useNavigate } from 'react-router-dom';
import numeral from 'numeral';

import Page from '../components/Controls/Page';
import { BaseAPI } from '../utils/Api';
import { getItem } from '../utils/Helper';
import Notification from '../utils/Notification';
import LoadingState from '../components/Controls/LoadingState';
import EmptyState from '../components/Controls/EmptyState';
import ErrorHandler from '../utils/ErrorHandler';

const Cart = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [cartList, setCartList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    getCartItems();
  }, []);

  const getCartItems = () => {
    setLoading(true);
    BaseAPI.get('/dataset/cartlist/my', { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
      .then((res) => {
        setCartList(res.data.data);
        const amount = res.data.data?.map((el) => ({ ...el, amount: el.price - el.discount }));
        setTotalAmount(amount.reduce((previous, current) => previous + current.amount, 0));
      })
      .catch((err) => {
        Notification(err?.response?.data?.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  const removeItemFromCart = (data) => {
    BaseAPI.post('/dataset/cartlist', { dataset_url: data }, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
      .then(() => {
        getCartItems();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const createOrder = () => {
    setLoading(true);
    let datasets = [];
    datasets = cartList?.map((el) => ({
      dataset_id: el.url_name,
      dataset_name: el.name,
      amount: el.price,
      discount: el.discount,
    }));

    const body = {
      datasets,
      total_amount: totalAmount,
    };

    BaseAPI.post('/order', body, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
      .then((res) => {
        console.log(res);
        getCartItems();
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          ErrorHandler(err.response?.data?.message, navigate);
        } else {
          Notification('Something went wrong', 'error');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Page title='Cart'>
      <LoadingState loading={loading} />
      <Typography variant='h4' mt={2} mb={4} className='bold'>
        My Cart
      </Typography>

      {cartList.length > 0 ? (
        <Grid container spacing={1}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, mb: 1 }} variant='outlined'>
              {cartList.map((el, i) => (
                <Grid container spacing={2} direction='row' justifyContent='space-between' alignItems='center' key={i}>
                  <Grid item xs={12} sm={10}>
                    <Grid spacing={2} container direction='row' justifyContent='center' alignItems='center'>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'start' } }}>
                        <img
                          src={`${process.env.REACT_APP_s3_cdn}/${process.env.REACT_APP_STAGE}/cover_images/${el.image}`}
                          alt='dataset'
                          height='120px'
                          width='auto'
                          style={{ borderRadius: '12px' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='overline'>{el.category}</Typography>
                        <br />
                        <Typography variant='subtitle1' component={Link} to={`/dataset-detail/${el.url_name}`} className='link'>
                          {el.name}
                        </Typography>
                        <Typography variant='subtitle2'>BDT {el.price - el.discount}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button fullWidth variant='text' color='error' startIcon={<CancelRoundedIcon />} onClick={() => removeItemFromCart(el.url_name)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Grid spacing={2} container flexDirection='column' alignItems='center' justifyContent='center'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>Total {numeral(totalAmount).format('0,0.00')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button fullWidth variant='contained' disableElevation mt={3} onClick={createOrder}>
                      Proceed to Checkout
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <EmptyState message='Your cart is empty 😟' />
      )}
    </Page>
  );
};

export default Cart;
